import { useSelector } from 'react-redux';
import type { Dealership } from '@makemydeal/dr-sales-checkin-types';
import type { Dealer } from '@makemydeal/dr-shared-types';
import type { DealershipsState, DealerState } from './types';
import type { SalesCheckinRootState } from '../common/types/SalesCheckinRootState';

const getDealer = (state: any) => state.dealer || {};

const getDealerSettings = (state: SalesCheckinRootState) => getDealer(state).dealerSettings;

const getDealerships = (state: SalesCheckinRootState) => state?.salesCheckin?.dealerships;

export const getIsEmployee = (state: SalesCheckinRootState): boolean | undefined => getDealerships(state)?.isEmployee;

export const getAvailableDealerships = (state: SalesCheckinRootState): Dealership[] =>
    getDealerships(state)?.availableDealerships ?? [];

export const getDealerDetails = (state: SalesCheckinRootState): DealerState => getDealer(state) as DealerState;

export const getCommonOrgId = (state: SalesCheckinRootState): string => {
    return getDealerDetails(state).commonOrgId || '';
};

export const getDealerMMDId = (state: SalesCheckinRootState): number | undefined => getDealerDetails(state).dealerMMDId;

export const useDealershipSelector = (): DealershipsState => {
    const state = useSelector((state: SalesCheckinRootState) => getDealerships(state) || {});
    return state;
};

export const useDealerDetailsSelector = (): DealerState => {
    return useSelector(getDealerDetails);
};

export const getTradeInValuationProvider = (state: SalesCheckinRootState): string =>
    getDealer(state).tradeInValuationProvider || '';

export const getDealerState = (state: SalesCheckinRootState): any => state.dealer;

export const getCreditProvider = (state: SalesCheckinRootState): string => getDealer(state).creditProvider || '';

export const getDealerPhone = (state: SalesCheckinRootState): string => getDealer(state).dealerPhone || '';

export const getDealerName = (state: SalesCheckinRootState): string => getDealer(state).dealerName || 'Dealer Name';

export const getDealerZip = (state: SalesCheckinRootState): string => getDealer(state).address?.zip || '';

/** Shapes dealer data for `/pdf-url`'s `dealerInfo` field, falling back to the SCI-specific `dealerName`/`dealerMMDId` fields when the `Dealer`-shaped ones aren't populated. */
export const getDealerInfoForPdf = (state: SalesCheckinRootState): Partial<Dealer> => {
    const dealer = getDealerDetails(state);

    return {
        dealerId: dealer.dealerMMDId ?? dealer.dealerId,
        name: dealer.name || dealer.dealerName,
        address: dealer.address,
        phone: dealer.phone,
        brandingExperienceLogo: dealer.brandingExperienceLogo
    };
};

export const isSubaruGtpEnabled = (state: SalesCheckinRootState): boolean => !!getDealerSettings(state)?.isSubaruGtpEnabled;

// TODO: Dealer Settins related selected need to move to separate dealersettings selectors
export const isAccelerateMyDealEnabled = (state: SalesCheckinRootState): boolean =>
    !!getDealerSettings(state)?.productSet?.accelerateMyDeal;

export const isDealCommentsSVEnabled = (state: SalesCheckinRootState): boolean => !!getDealerSettings(state)?.enableDealCommentsSV;

export const getTradeInHideExcellentConditionWebsite = (state: SalesCheckinRootState): boolean =>
    !!getDealerSettings(state)?.getTradeInHideExcellentConditionWebsite;

export const getTradeInHideExcellentConditionListingSite = (state: SalesCheckinRootState): boolean =>
    !!getDealerSettings(state)?.getTradeInHideExcellentConditionListingSite;
